<template>
  <div class="mt-4" id="mainPettyCashHistory">
    <div class="rounded-lg overflow-hidden border bg-white">
      <div class="py-4 px-3 flex items-center justify-between gap-4 flex-wrap">
        <div class="flex items-center gap-4 flex-wrap">
          <select
            v-model="limitRegister"
            class="bg-white border py-2 text-sm px-3 text-gray-600 cursor-pointer rounded-lg outline-none min-w-[140px] font-semibold"
            @change="resetValuesAndGetData"
          >
            <option value="10">10 Registros</option>
            <option value="25">25 Registros</option>
            <option value="50">50 Registros</option>
            <option value="100">100 Registros</option>
          </select>
        </div>
        <div class="w-full md:w-[420px]">
          <input
            type="text"
            class="py-2 px-2 text-sm bg-white border outline-none rounded-lg shadow-sm text-gray-600 w-full"
            placeholder="Buscar por responsable, comentario o ID..."
            v-model="search"
            @keydown.enter="resetValuesAndGetData"
          />
        </div>
      </div>

      <div class="w-full resize-x resize-generate" ref="widthNav" @resize="handleResize">
        <div class="overflow-x-auto">
          <table class="table-auto w-full">
            <TableHead :headers="header" />
            <LoadingTables v-if="loading" :columns="8" />
            <tbody class="text-gray-500 text-left border-t-[1px]" v-else>
              <tr
                v-for="(register, index) in registers"
                :key="index"
                class="hover:bg-blue-500 transition-colors hover:text-white capitalize font-semibold odd:bg-white even:bg-slate-50"
                @click="selectRow($event)"
              >
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap">#{{ register.id }}</td>
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">{{ register.usuario_apertura }}</td>
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap">{{ formatDateTime(register.fecha_apertura) }}</td>
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap">{{ money() }} {{ format(register.monto_inicial) }}</td>
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap text-red-500">{{ money() }} {{ format(register.total_desembolsado) }}</td>
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap text-green-600">{{ money() }} {{ format(register.balance_actual) }}</td>
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap text-right">
                  <div class="flex items-center justify-end gap-3">
                    <span class="px-3 py-1 rounded-lg text-sm bg-green-50 text-green-600">
                      ACTIVA
                    </span>
                    <button
                      class="bg-blue-50 text-blue-500 px-3 py-1 rounded-md text-sm"
                      @click.stop="openDetails(register.id)"
                    >
                      Ver detalle
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <TablePagination
        :currentPage="currentPage"
        :total="totalPage"
        :next="next"
        :prev="prev"
      />
    </div>
  </div>

  <ShowPettyCashHistory
    v-if="stateShowDetail"
    :activeModal="toggleShowDetail"
    :id="selectedId"
  />
</template>

<script>
import { onMounted, ref, watchEffect } from "vue";
import axios from "@/api";
import LoadingTables from "@/components/Loadings/LoadingTables";
import TableHead from "@/components/TablesComponents/TableHead";
import TablePagination from "@/components/TablesComponents/TablePagination.vue";
import ShowPettyCashHistory from "@/components/PettyCash/ShowPettyCashHistory.vue";
import { errorActions } from "@/alerts";
import { observeElement } from "@/observer";
import { idLocalStore } from "@/publicjs/localStorage";
import { money, format } from "@/publicjs/money";
import { formatDateTime } from "@/publicjs/formatDate";
import { selectRow } from "@/publicjs/selectRowTable";

export default {
  name: "MainPettyCashHistory",
  components: {
    LoadingTables,
    TableHead,
    TablePagination,
    ShowPettyCashHistory,
  },
  setup() {
    const loading = ref(true);
    const registers = ref([]);
    const currentPage = ref(1);
    const limitRegister = ref(10);
    const totalPage = ref(0);
    const search = ref("");
    const registerPaginate = ref(null);
    const idStore = ref(0);
    const widthNav = ref(null);
    const stateShowDetail = ref(false);
    const selectedId = ref(0);
    const header = [
      [
        { title: "id" },
        { title: "responsable" },
        { title: "apertura" },
        { title: "monto inicial" },
        { title: "desembolsado" },
        { title: "balance" },
        { title: "estado" },
      ],
    ];

    const handleResize = () => {
      const nav = document.querySelector("nav")?.offsetWidth || 0;
      watchEffect(() => {
        if (widthNav.value) {
          widthNav.value.style.width = `calc(100vw - ${nav + 42}px)`;
        }
      });
    };

    const getData = async () => {
      loading.value = true;
      const response = await axios
        .get(
          `caja-chica?page=${currentPage.value}&per_page=${limitRegister.value}&search=${search.value}&idstore=${idStore.value}&state=0`,
        )
        .catch((error) => errorActions(error));

      if (!response) {
        loading.value = false;
        return;
      }

      registers.value = await response.data.data;
      registerPaginate.value = await response.data;
      totalPage.value = registerPaginate.value.last_page;
      loading.value = false;
    };

    const resetValuesAndGetData = async () => {
      currentPage.value = 1;
      await getData();
    };

    const next = async () => {
      totalPage.value = registerPaginate.value.last_page;
      if (currentPage.value <= totalPage.value - 1) currentPage.value++;
      await getData();
    };

    const prev = async () => {
      totalPage.value = registerPaginate.value.last_page;
      if (currentPage.value >= 2) currentPage.value--;
      await getData();
    };

    const toggleShowDetail = () => {
      stateShowDetail.value = !stateShowDetail.value;
    };

    const openDetails = (id) => {
      selectedId.value = id;
      toggleShowDetail();
    };

    onMounted(async () => {
      observeElement("#mainPettyCashHistory");
      handleResize();
      window.addEventListener("resize", handleResize);
      idStore.value = await idLocalStore();
      await getData();
    });

    return {
      loading,
      registers,
      currentPage,
      limitRegister,
      totalPage,
      search,
      widthNav,
      header,
      handleResize,
      resetValuesAndGetData,
      next,
      prev,
      money,
      format,
      formatDateTime,
      selectRow,
      stateShowDetail,
      selectedId,
      toggleShowDetail,
      openDetails,
    };
  },
};
</script>